import type { TFunction } from 'i18next';

import { gameKey, gameTitle, type GameSourceRecord } from './game-sources';
import { filterTerms, foldText, providerLabelOf } from './site-games';

/**
 * Filtering the game catalogue: by who made a game, what kind it is, and what it is about.
 *
 * The catalogue carries no ready-made facets, only metadata attributes, and they are uneven:
 *  - `provider_id` sits on every game.
 *  - `game_type` (SLOTS, TABLE, ARCADE, BINGO, CRASH) on 99%, `game_subtype` (BLACKJACK, ROULETTE,
 *    PLINKO…) on 86%. They read as a hierarchy but the data does NOT honour it — the subtype "SLOTS"
 *    turns up under TABLE, BINGO and CRASH — so the tree here is built FROM the catalogue rather than
 *    declared, and a type also matches its games that carry no subtype at all (518 of them).
 *  - `visual_theme` on 59%, and in two shapes: a plain string on most, a one-element array on the rest,
 *    which is why the raw values look duplicated (`FRUIT` and `['FRUIT']`). Reading through the array
 *    collapses 169 raw values to 81 real themes.
 */

const TYPE_FIELD = 'metadata_game_attribute_game_type';
const SUBTYPE_FIELD = 'metadata_game_attribute_game_subtype';
const THEME_FIELD = 'metadata_game_attribute_visual_theme';

/** Separates a type from its subtype in a selection key: `TABLE:BLACKJACK`. */
const SUB_SEP = ':';

const asText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** `ACTION_&_ADVENTURE` reads as `Action & adventure`: catalogue data, shown as given, not translated. */
export const facetLabel = (raw: string): string => {
  const words = raw.replace(/_/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const gameTypeOf = (game: GameSourceRecord): string => asText(game[TYPE_FIELD]);
export const gameSubtypeOf = (game: GameSourceRecord): string => asText(game[SUBTYPE_FIELD]);

/** A game's themes, read through the two shapes the attribute takes, so both collapse to one value. */
export const gameThemesOf = (game: GameSourceRecord): string[] => {
  const value = game[THEME_FIELD];
  const values = Array.isArray(value) ? value : [value];
  const themes: string[] = [];
  values.forEach((entry) => {
    const text = asText(entry);
    if (text !== '' && !themes.includes(text)) themes.push(text);
  });
  return themes;
};

/** The selection keys a game answers to: its type, and its type+subtype pair when it has one. */
export const typeKeysOf = (game: GameSourceRecord): string[] => {
  const type = gameTypeOf(game);
  if (type === '') return [];
  const subtype = gameSubtypeOf(game);
  return subtype === '' ? [type] : [type, `${type}${SUB_SEP}${subtype}`];
};

export type FacetOption = { value: string; label: string; count: number };
export type TypeOption = FacetOption & { subs: FacetOption[] };

export type GameFilters = {
  /** Provider ids. Empty means every provider. */
  providers: string[];
  /** Type keys — `SLOTS` for a whole type, `TABLE:BLACKJACK` for one subtype. Empty means every kind. */
  types: string[];
  /** Raw theme values. Empty means every theme. */
  themes: string[];
  /** Free text, matched against the title, the id and the maker's name. */
  query: string;
};

export const NO_FILTERS: GameFilters = { providers: [], types: [], themes: [], query: '' };

/** What an automatic list is built from: the three facets, without the search term, which narrows a browse
 *  but is never part of a list's rule. */
export type ListFilters = Pick<GameFilters, 'providers' | 'types' | 'themes'>;

export const NO_LIST_FILTERS: ListFilters = { providers: [], types: [], themes: [] };

/** The rule part of a set of filters. */
export const listFiltersOf = (filters: ListFilters): ListFilters => ({
  providers: [...filters.providers],
  types: [...filters.types],
  themes: [...filters.themes],
});

/** Whether any facet has a pick. An automatic list needs one: with none it would be the whole catalogue. */
export const hasListFilters = (filters: ListFilters): boolean =>
  filters.providers.length + filters.types.length + filters.themes.length > 0;

/** The same picks, whatever order they were made in. */
export const sameListFilters = (a: ListFilters, b: ListFilters): boolean => {
  const same = (x: readonly string[], y: readonly string[]): boolean =>
    x.length === y.length && x.every((value) => y.includes(value)) && y.every((value) => x.includes(value));
  return same(a.providers, b.providers) && same(a.types, b.types) && same(a.themes, b.themes);
};

/** `terms` comes prepared, because this runs once per game and the query is the same for all of them. */
const matchesTerms = (
  game: GameSourceRecord,
  terms: readonly string[],
  providerNames: Record<string, string>,
  t: TFunction,
): boolean => {
  if (terms.length === 0) return true;
  // The same matching the list's own search box uses (site-games): accent-folded, and EVERY word has to
  // appear somewhere in the name, the ID or the provider. Two search boxes in one popup answering
  // differently is the kind of thing people notice: "crash pragmatic" should narrow, not find nothing.
  const haystack = foldText(`${gameTitle(game)} ${gameKey(game)} ${providerLabelOf(game, providerNames, t)}`);
  return terms.every((term) => haystack.includes(term));
};

const matchesProviders = (game: GameSourceRecord, providers: string[]): boolean =>
  providers.length === 0 || providers.includes(String(game.provider_id ?? ''));

/** A type selection matches its whole type; a subtype selection only that pair. */
const matchesTypes = (game: GameSourceRecord, types: string[]): boolean =>
  types.length === 0 || typeKeysOf(game).some((key) => types.includes(key));

const matchesThemes = (game: GameSourceRecord, themes: string[]): boolean =>
  themes.length === 0 || gameThemesOf(game).some((theme) => themes.includes(theme));

/**
 * A predicate for the facets alone: what an automatic list's rule is. spark-creatives applies the same rule
 * (a port of this file's matching) when it writes the list to the site and when it refreshes it, so what the
 * hub shows and what the site shows agree game for game; a change here must be made there too.
 */
export const listFilter = (filters: ListFilters): ((game: GameSourceRecord) => boolean) => (game) =>
  matchesProviders(game, filters.providers) &&
  matchesTypes(game, filters.types) &&
  matchesThemes(game, filters.themes);

/**
 * A predicate for one set of filters. Built once per pass so the search query is folded and split ONCE
 * rather than per game: these passes run over the whole catalogue, which is thousands of records.
 */
export const gameFilter = (
  filters: GameFilters,
  providerNames: Record<string, string>,
  t: TFunction,
): ((game: GameSourceRecord) => boolean) => {
  const terms = filterTerms(filters.query);
  const facets = listFilter(filters);
  return (game) => facets(game) && matchesTerms(game, terms, providerNames, t);
};

/**
 * The games an automatic list holds, in its order: first the games of `order` (the owner's order, empty until
 * they reorder) that still match, as ordered, then every other matching game in catalogue order, which is where
 * a game the platform adds later lands. An id in `order` that no longer matches is not a member, and nothing
 * shows for it. With no pick at all the list holds nothing rather than the whole catalogue.
 */
export const resolveAutoList = (
  catalogue: readonly GameSourceRecord[],
  filters: ListFilters,
  order: readonly string[],
): GameSourceRecord[] => {
  if (!hasListFilters(filters)) return [];
  const matches = listFilter(filters);
  const matching = catalogue.filter(matches);
  const byId = new Map(matching.map((game) => [gameKey(game), game]));
  const placed = new Set<string>();
  const out: GameSourceRecord[] = [];
  order.forEach((id) => {
    const game = byId.get(id);
    if (game && !placed.has(id)) {
      placed.add(id);
      out.push(game);
    }
  });
  matching.forEach((game) => {
    const id = gameKey(game);
    if (!placed.has(id)) {
      placed.add(id);
      out.push(game);
    }
  });
  return out;
};

/** One game against one set of filters. Prefer `gameFilter` when testing more than a single game. */
export const gameMatchesFilters = (
  game: GameSourceRecord,
  filters: GameFilters,
  providerNames: Record<string, string>,
  t: TFunction,
): boolean => gameFilter(filters, providerNames, t)(game);

/**
 * The options for one facet, each counted as it WOULD land: every other facet applied, this one left
 * open. That is what makes a count read as "picking this leaves me N games" rather than counting the
 * catalogue blind.
 */
const countingSet = (
  games: readonly GameSourceRecord[],
  filters: GameFilters,
  open: 'providers' | 'types' | 'themes',
  providerNames: Record<string, string>,
  t: TFunction,
): GameSourceRecord[] => games.filter(gameFilter({ ...filters, [open]: [] }, providerNames, t));

/** An option earns its place when picking it would still leave games, or when it is already picked:
 *  a selection must always be visible so it can be undone, even once other facets narrow it to nothing. */
const worthShowing = (option: { value: string; count: number }, selected: readonly string[]): boolean =>
  option.count > 0 || selected.includes(option.value);

export const providerOptions = (
  games: readonly GameSourceRecord[],
  filters: GameFilters,
  providerNames: Record<string, string>,
  t: TFunction,
): FacetOption[] => {
  const counted = countingSet(games, filters, 'providers', providerNames, t);
  const labels = new Map<string, string>();
  const counts = new Map<string, number>();
  games.forEach((game) => {
    const id = String(game.provider_id ?? '');
    if (id === '') return;
    if (!labels.has(id)) labels.set(id, providerLabelOf(game, providerNames, t));
  });
  counted.forEach((game) => {
    const id = String(game.provider_id ?? '');
    if (id !== '') counts.set(id, (counts.get(id) ?? 0) + 1);
  });
  return [...labels.entries()]
    .map(([value, label]) => ({ value, label, count: counts.get(value) ?? 0 }))
    .filter((option) => worthShowing(option, filters.providers))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const typeOptions = (
  games: readonly GameSourceRecord[],
  filters: GameFilters,
  providerNames: Record<string, string>,
  t: TFunction,
): TypeOption[] => {
  const counted = countingSet(games, filters, 'types', providerNames, t);
  // The tree as the catalogue actually has it, not as the naming suggests.
  const tree = new Map<string, Set<string>>();
  games.forEach((game) => {
    const type = gameTypeOf(game);
    if (type === '') return;
    if (!tree.has(type)) tree.set(type, new Set());
    const subtype = gameSubtypeOf(game);
    if (subtype !== '') tree.get(type)?.add(subtype);
  });
  // Every key counted in ONE pass over the narrowed set. Counting each key with its own scan meant a
  // full walk of the catalogue per type AND per subtype, every time a filter or a keystroke changed.
  const counts = new Map<string, number>();
  counted.forEach((game) => {
    typeKeysOf(game).forEach((key) => counts.set(key, (counts.get(key) ?? 0) + 1));
  });
  const countOf = (key: string): number => counts.get(key) ?? 0;

  return [...tree.entries()]
    .map(([type, subtypes]) => ({
      value: type,
      label: facetLabel(type),
      count: countOf(type),
      subs: [...subtypes]
        .map((subtype) => ({
          value: `${type}${SUB_SEP}${subtype}`,
          label: facetLabel(subtype),
          count: countOf(`${type}${SUB_SEP}${subtype}`),
        }))
        .filter((sub) => worthShowing(sub, filters.types))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
    }))
    .filter((type) => type.count > 0 || type.subs.length > 0 || filters.types.includes(type.value))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
};

export const themeOptions = (
  games: readonly GameSourceRecord[],
  filters: GameFilters,
  providerNames: Record<string, string>,
  t: TFunction,
): FacetOption[] => {
  const counted = countingSet(games, filters, 'themes', providerNames, t);
  const labels = new Map<string, string>();
  const counts = new Map<string, number>();
  games.forEach((game) => gameThemesOf(game).forEach((theme) => labels.set(theme, facetLabel(theme))));
  counted.forEach((game) =>
    gameThemesOf(game).forEach((theme) => counts.set(theme, (counts.get(theme) ?? 0) + 1)),
  );
  return [...labels.entries()]
    .map(([value, label]) => ({ value, label, count: counts.get(value) ?? 0 }))
    .filter((option) => worthShowing(option, filters.themes))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
};

/** Add or remove one value from a facet's selection. */
export const toggleFacet = (selected: readonly string[], value: string): string[] =>
  selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value];

/**
 * Ticking a whole type replaces any of its subtype picks; unticking clears them too, so the parent box
 * and its children never disagree.
 */
export const toggleType = (selected: readonly string[], type: TypeOption): string[] => {
  const mine = [type.value, ...type.subs.map((sub) => sub.value)];
  const others = selected.filter((value) => !mine.includes(value));
  const wholeOn = selected.includes(type.value);
  return wholeOn ? others : [...others, type.value];
};

/**
 * Picking a subtype NARROWS: the whole-type key comes off, or the type would keep matching everything
 * under it and the pick would read as doing nothing.
 */
export const toggleSubtype = (selected: readonly string[], type: TypeOption, subValue: string): string[] => {
  const withoutWhole = selected.filter((value) => value !== type.value);
  return toggleFacet(withoutWhole, subValue);
};

/** Whether a type box reads as ticked, part-ticked (some subtypes), or empty. */
export const typeBoxState = (selected: readonly string[], type: TypeOption): 'on' | 'partial' | 'off' => {
  if (selected.includes(type.value)) return 'on';
  return type.subs.some((sub) => selected.includes(sub.value)) ? 'partial' : 'off';
};
